import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import { Box, Button, Typography, useTheme } from '@mui/material';
import LogRocket from 'logrocket';
import type { ReactElement } from 'react';

import type { FilterByOpts } from '../../../utils/ApiClient';
import type { PossiblyAsyncResult } from '../../../utils/Query';
import { LoadingSpinner } from '../../LoadingSpinner/LoadingSpinner';

export type TabLabel<TRow extends { id: string }> = {
  /** The label to display on each tab */
  label: string;
  additionalLabels?: string[];
  /**
   * The value to filter against.  If a tab with a `null` filterValue becomes
   * active, then that action should implicitly deactivate all other tabs, and
   * activating any other tab should implicitly deactivate the tab whose
   * filterValue is `null`.
   */
  filterValue: FilterByOpts<TRow>['values'][0] | null;
  resultCount?: number;
  isActive: boolean;
};

export type FilterTabsProps<TRow extends { id: string }, TAgg extends { id: string }> = {
  tabs: TabLabel<TRow>[];
  aggregations: PossiblyAsyncResult<{ [id: string]: TAgg }>;
  handleChangeTab: (filter: TabLabel<TRow>, index: number) => void;
};

export const FilterTabs = <TRow extends { id: string }, TAgg extends { id: string }>({
  tabs,
  aggregations,
  handleChangeTab,
}: FilterTabsProps<TRow, TAgg>): ReactElement => {
  const theme = useTheme();

  if (aggregations === undefined) {
    return <LoadingSpinner size={24} />;
  }

  const renderAdditionalLabels = ({
    filter,
    color,
  }: {
    filter: TabLabel<TRow>;
    color: string;
  }): ReactElement | ReactElement[] | null => {
    if (filter.additionalLabels === undefined) {
      return null;
    }
    const loadingSpinner = (
      <Box sx={{ lineHeight: 1, marginTop: '4px', marginBottom: '2px' }}>
        <LoadingSpinner size={20} sx={{ color }} color='inherit' wrapInBox={false} />
      </Box>
    );
    // If we are still loading from an API response, show a spinner
    if (aggregations.isLoading || aggregations.data === undefined) {
      return loadingSpinner;
    }
    if (aggregations.isError) {
      // If an error occurred, hide it since it's unclear how to best display it
      // to a user.
      return null;
    }

    // The aggregated data is an empty object. This is an unexpected state.
    // See https://app.shortcut.com/slabstack/story/2821/fix-possible-undefined-in-additionallabels
    if (Object.keys(aggregations.data).length === 0) {
      // Hit a spot where data is not ready, so we don't know what to display
      // This is a temporary state, so we don't want to like, load, and LogRocket.
      // We use error because it will alert in the logrockets alert channel.
      LogRocket.error('Aggregated data is unexpectedly empty', { filter, aggregations });
      return loadingSpinner;
    }

    return filter.additionalLabels.map((subLabel) => (
      <Typography variant='body2' color={color} key={color} noWrap>
        {subLabel}
      </Typography>
    ));
  };

  const renderResultCount = ({
    filter,
    color,
    backgroundColor,
  }: {
    filter: TabLabel<TRow>;
    color: string;
    backgroundColor: string;
  }): ReactElement | ReactElement[] | null => {
    if (filter.resultCount === undefined || aggregations.isError) {
      return null;
    }
    return (
      <Box paddingX='0.875rem' borderRadius='0.5rem' height='fit-content' sx={{ backgroundColor }}>
        {aggregations.isLoading || aggregations.data === undefined ? (
          <LoadingSpinner
            size={14}
            sx={{
              margin: 'auto',
              verticalAlign: 'middle',
              color,
            }}
            wrapInBox={false}
          />
        ) : (
          <Typography variant='body3' fontSize='0.875rem' color={color}>
            {filter.resultCount}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    // NOTE: We do not use MUI `Tabs` here because:
    //   1. It must have `Tab`s nested underneath, which do not have enough customization
    //   2. If used without `Tab`s, throws prop-injection warnings
    <Box
      display='flex'
      sx={{
        borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
        // -webkit-scrollbar properties for Safari
        '::-webkit-scrollbar': {
          background: theme.palette.background.default,
          height: '10px',
        },
        '::-webkit-scrollbar-thumb': {
          border: `2px solid ${theme.palette.background.default}`,
          borderRadius: '5px',
          background: theme.palette.SlabGray['400'],
          padding: '2px',
        },
        // overflow-x, not overflow, so that we don't get a vertical scrollbar :-D
        overflowX: 'auto',
        // standard scrollbar properties for everyone else
        scrollbarColor: `${theme.palette.SlabGray['400']} ${theme.palette.background.default}`,
        scrollbarWidth: 'thin',
      }}
    >
      {tabs.map((f, idx) => {
        const additionalLabelColor = f.isActive
          ? theme.palette.SlabGray['600']
          : theme.palette.SlabGray['500'];
        const resultCountColor = f.isActive
          ? theme.palette.SlabGray[0]
          : theme.palette.SlabGray['600'];
        const resultCountBackground = f.isActive
          ? theme.palette.SlabIndigo['500']
          : theme.palette.SlabGray.Stroke;
        return (
          <Button
            key={`filter-${f.label}`}
            disableRipple
            id={`project-filter-tab-${idx}`}
            onClick={(): void => handleChangeTab(f, idx)}
            sx={{
              minWidth: 'auto',
              borderRadius: 0,
              borderBottom: f.isActive ? `1px solid ${theme.palette.SlabGray['800']}` : 'none',
              textTransform: 'none',
              alignItems: 'start',
            }}
          >
            {f.isActive ? (
              <CheckBox sx={{ color: theme.palette.SlabIndigo[700] }} />
            ) : (
              <CheckBoxOutlineBlank sx={{ color: theme.palette.SlabGray[600] }} />
            )}
            <Box paddingLeft='0.5rem' paddingRight='1rem' display='flex' gap='0.625rem'>
              <Box display='flex' flexDirection='column' textAlign='left'>
                <Typography
                  variant='body1'
                  noWrap
                  color={f.isActive ? theme.palette.SlabGray['800'] : theme.palette.SlabGray['600']}
                >
                  {f.label}
                </Typography>
                {renderAdditionalLabels({ filter: f, color: additionalLabelColor })}
              </Box>
              {renderResultCount({
                filter: f,
                color: resultCountColor,
                backgroundColor: resultCountBackground,
              })}
            </Box>
          </Button>
        );
      })}
    </Box>
  );
};
